import React, { useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { Smartphone, Globe, Server, CreditCard, PenTool, Workflow } from 'lucide-react';
import { finePointer } from './motion';

gsap.registerPlugin(ScrollTrigger, useGSAP);

const SERVICES = [
  { title: 'Apps iOS e Android', desc: 'React Native e Expo, UX otimizada e publicação nas lojas.', Icon: Smartphone },
  { title: 'Plataformas web', desc: 'Sistemas rápidos e escaláveis com React e Next.js, de dashboards a portais.', Icon: Globe },
  { title: 'Backend e APIs', desc: 'Node.js, bancos de dados e integrações prontas para crescer.', Icon: Server },
  { title: 'Pagamentos', desc: 'Planos, licenças e assinaturas recorrentes com Stripe.', Icon: CreditCard },
  { title: 'Design de produto', desc: 'Interfaces claras, desenhadas para conversão e retenção.', Icon: PenTool },
  { title: 'Integrações', desc: 'Webhooks, notificações e automações entre serviços.', Icon: Workflow },
];

// clip-path de cada lado: a faixa verde entra e sai pelo lado do cursor.
const HIDE_TOP = 'inset(0% 0% 100% 0%)';
const HIDE_BOTTOM = 'inset(100% 0% 0% 0%)';
const SHOW = 'inset(0% 0% 0% 0%)';

function Row({ s }) {
  const content = (Title) => (
    <>
      <Title className="hv3-svc-title">{s.title}</Title>
      <p className="hv3-svc-desc">{s.desc}</p>
      <span className="hv3-svc-icon" aria-hidden>
        <s.Icon size={28} strokeWidth={1.5} />
      </span>
    </>
  );
  return (
    <li className="hv3-svc">
      <div className="hv3-svc-base">{content('h3')}</div>
      {/* Cópia exata do conteúdo com as cores invertidas, recortada pelo clip-path:
          a troca de cor acompanha a borda da faixa com precisão de pixel. */}
      <div className="hv3-svc-fill" aria-hidden>
        {content('span')}
      </div>
    </li>
  );
}

function Services() {
  const root = useRef(null);

  useGSAP(
    () => {
      const q = gsap.utils.selector(root);
      const mm = gsap.matchMedia();

      mm.add('(prefers-reduced-motion: no-preference)', () => {
        gsap.from(q('.hv3-svc'), {
          yPercent: 40,
          autoAlpha: 0,
          duration: 1.1,
          ease: 'expo.out',
          stagger: 0.06,
          scrollTrigger: { trigger: q('.hv3-svc-list'), start: 'top 80%' },
        });
      });

      if (!finePointer()) {
        // Toque: a linha que atravessa o meio da tela acende, e a faixa entra
        // pelo lado para onde a pessoa está rolando.
        const triggers = q('.hv3-svc').map((row) => {
          const fill = row.querySelector('.hv3-svc-fill');
          gsap.set(fill, { clipPath: HIDE_BOTTOM });
          const to = (clipPath, from) =>
            gsap.fromTo(fill, { clipPath: from }, { clipPath, duration: 0.5, ease: 'expo.out', overwrite: true });
          return ScrollTrigger.create({
            trigger: row,
            start: 'top 60%',
            end: 'bottom 40%',
            onEnter: () => to(SHOW, HIDE_BOTTOM),
            onLeave: () => to(HIDE_TOP, SHOW),
            onEnterBack: () => to(SHOW, HIDE_TOP),
            onLeaveBack: () => to(HIDE_BOTTOM, SHOW),
          });
        });
        return () => {
          triggers.forEach((t) => t.kill());
          mm.revert();
        };
      }

      const cleanups = q('.hv3-svc').map((row) => {
        const fill = row.querySelector('.hv3-svc-fill');
        gsap.set(fill, { clipPath: HIDE_BOTTOM });
        const fromTop = (e) => {
          const r = row.getBoundingClientRect();
          return e.clientY < r.top + r.height / 2;
        };
        const enter = (e) => {
          gsap.fromTo(
            fill,
            { clipPath: fromTop(e) ? HIDE_TOP : HIDE_BOTTOM },
            { clipPath: SHOW, duration: 0.42, ease: 'expo.out', overwrite: true }
          );
        };
        const leave = (e) => {
          gsap.to(fill, { clipPath: fromTop(e) ? HIDE_TOP : HIDE_BOTTOM, duration: 0.32, ease: 'power3.out', overwrite: true });
        };
        row.addEventListener('pointerenter', enter);
        row.addEventListener('pointerleave', leave);
        return () => {
          row.removeEventListener('pointerenter', enter);
          row.removeEventListener('pointerleave', leave);
        };
      });

      return () => {
        cleanups.forEach((fn) => fn());
        mm.revert();
      };
    },
    { scope: root }
  );

  return (
    <section className="hv3-services" ref={root} aria-labelledby="servicos-title">
      <div className="hv3-wrap">
        <h2 id="servicos-title" className="hv3-h2">
          Tudo que um produto digital precisa, <span className="hv3-dim">em um só time.</span>
        </h2>
      </div>
      <ul className="hv3-svc-list">
        {SERVICES.map((s) => (
          <Row s={s} key={s.title} />
        ))}
      </ul>
    </section>
  );
}

export default Services;
